/**
 * The question editor, generated from the plugin's JSON Schema.
 *
 * This is why adding a question type needs no front-end work: the fields, their
 * labels and their help text all come from `fields_schema` on
 * `/worksheets/question-types`, which is the plugin's own Pydantic model. Writing
 * eight hand-made editors would put the labels in two places and guarantee they
 * drift.
 *
 * Saving is on blur, not on every keystroke: a request per character would be a
 * lot of traffic and would fight the teacher's cursor. The screen reports when a
 * change was actually stored rather than claiming it continuously.
 */

import { useEffect, useRef, useState } from 'react'
import { Checkbox, Field, Input, Select, Textarea } from '../../components/ui'
import { useAiTextAssist } from './AiTextAssist'
import { SymbolPicker } from './SymbolPicker'
import { Icon } from '../../components/icons'

type JsonSchema = {
  properties?: Record<string, SchemaProperty>
  required?: string[]
  $defs?: Record<string, { enum?: string[] }>
}

type SchemaProperty = {
  /** Stored for papers made before a change, never shown. */
  legacy?: boolean
  advanced?: boolean
  title?: string
  description?: string
  type?: string
  enum?: string[]
  default?: unknown
  minimum?: number
  maximum?: number
  allOf?: { $ref?: string }[]
  $ref?: string
  anyOf?: { type?: string; $ref?: string }[]
}

/** Long-form fields get a textarea; everything else a single line. */
const MULTILINE = new Set([
  'stem',
  'prompt',
  'question',
  'sentence',
  'statement',
  'instructions',
  'text',
  'expected_answer',
])

type FieldsProps = {
  schema: JsonSchema
  value: Record<string, unknown>
  disabled?: boolean
  onCommit: (patch: Record<string, unknown>) => void
}

/** A legacy field is storage kept so an old paper still prints. Showing it would
 *  offer a teacher a control that does nothing. */
function usableProperties(schema: JsonSchema) {
  return Object.entries(schema.properties ?? {}).filter(([, property]) => !property.legacy)
}

function renderFields(
  entries: [string, SchemaProperty][],
  { schema, value, disabled, onCommit }: FieldsProps,
) {
  return entries.map(([name, property]) => (
    <SchemaField
      key={name}
      name={name}
      property={property}
      schema={schema}
      value={value[name]}
      disabled={disabled}
      onCommit={(next) => onCommit({ [name]: next })}
    />
  ))
}

/** What the pupil reads, and the answer content. Nothing about page layout. */
export function SchemaFields(props: FieldsProps) {
  const regular = usableProperties(props.schema).filter(([, property]) => !property.advanced)
  return <div className="tc-schema-fields">{renderFields(regular, props)}</div>
}

/** True when a question type has anything to put in the optional section, so a
 *  caller can skip rendering an empty one. */
export function hasOptionalSettings(schema: JsonSchema): boolean {
  return usableProperties(schema).some(([, property]) => property.advanced)
}

/**
 * How the question is arranged on the page.
 *
 * Rendered separately from the fields above so the caller can place it at the
 * very bottom of the question, after the answers. Kept inside SchemaFields it
 * appeared between the question and its own options - so a teacher writing a
 * multiple choice question had to step over "Optional settings" to reach the
 * choices, which is the opposite of putting it out of the way.
 */
export function OptionalSettings(props: FieldsProps) {
  const advanced = usableProperties(props.schema).filter(([, property]) => property.advanced)
  if (advanced.length === 0) return null

  return (
    <details className="tc-advanced-fields">
      <summary>
        <span className="tc-advanced-chevron" aria-hidden="true">
          <Icon.chevronRight size={14} />
        </span>
        <span className="tc-advanced-title">Optional settings</span>
        <span className="tc-advanced-count">{advanced.length}</span>
        <span className="tc-advanced-hint">how this looks on the page</span>
      </summary>
      <div className="tc-schema-fields">{renderFields(advanced, props)}</div>
    </details>
  )
}

function SchemaField({
  name,
  property,
  schema,
  value,
  disabled,
  onCommit,
}: {
  name: string
  property: SchemaProperty
  schema: JsonSchema
  value: unknown
  disabled?: boolean
  onCommit: (value: unknown) => void
}) {
  // Local state so typing stays responsive; the value is sent on blur.
  const [draft, setDraft] = useState(value ?? '')
  const textRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)
  useEffect(() => setDraft(value ?? ''), [value])

  // Called for every field, used only by the long ones below: hooks cannot sit
  // behind the early returns for select, checkbox and number fields.
  const assist = useAiTextAssist({
    text: String(draft ?? ''),
    disabled: disabled || !MULTILINE.has(name),
    onApply: (next) => {
      setDraft(next)
      onCommit(next)
    },
  })

  const label = property.title ?? humanise(name)
  const hint = property.description
  const choices = enumChoices(property, schema)

  if (choices) {
    return (
      <Field label={label} hint={hint}>
        <Select
          value={String(draft ?? '')}
          disabled={disabled}
          onChange={(event) => {
            setDraft(event.target.value)
            onCommit(event.target.value)
          }}
        >
          {choices.map((choice) => (
            <option key={choice} value={choice}>
              {OPTION_LAYOUT_LABELS[choice] ?? humanise(choice)}
            </option>
          ))}
        </Select>
      </Field>
    )
  }

  if (isType(property, 'boolean')) {
    return (
      <Checkbox
        label={label}
        checked={Boolean(draft)}
        disabled={disabled}
        title={hint}
        onChange={(event) => {
          setDraft(event.target.checked)
          onCommit(event.target.checked)
        }}
      />
    )
  }

  if (isType(property, 'integer') || isType(property, 'number')) {
    return (
      <Field label={label} hint={hint}>
        <Input
          type="number"
          min={property.minimum}
          max={property.maximum}
          value={String(draft ?? '')}
          disabled={disabled}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => onCommit(draft === '' ? null : Number(draft))}
        />
      </Field>
    )
  }

  const multiline = MULTILINE.has(name)

  /** Insert at the cursor, not at the end - a symbol belongs where the teacher
   *  was typing. The selection is restored afterwards so typing simply carries
   *  on past the inserted character. */
  const insertAtCursor = (symbol: string) => {
    const control = textRef.current
    const current = String(draft ?? '')
    const start = control?.selectionStart ?? current.length
    const end = control?.selectionEnd ?? start
    const next = `${current.slice(0, start)}${symbol}${current.slice(end)}`
    setDraft(next)
    onCommit(next)
    requestAnimationFrame(() => {
      control?.focus()
      const caret = start + symbol.length
      control?.setSelectionRange(caret, caret)
    })
  }

  return (
    <div className={`tc-text-field${multiline ? ' tc-text-field-multiline' : ''}`}>
      {/* Label on the left, tools on the right, on one line above the field.
          The tools used to sit in a column beside the field, which read as a
          stray group of buttons floating next to the text rather than as
          things belonging to it. */}
      <div className="tc-field-bar">
        <span className="tc-field-label" id={`${name}-label`}>
          {label}
        </span>
        <div className="tc-field-bar-tools">
          <SymbolPicker disabled={disabled} onPick={insertAtCursor} />
          {multiline && assist.action}
        </div>
      </div>

      {multiline ? (
        <Textarea
          ref={(element) => {
            textRef.current = element
          }}
          aria-labelledby={`${name}-label`}
          value={String(draft ?? '')}
          disabled={disabled}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => onCommit(draft)}
        />
      ) : (
        <Input
          ref={(element) => {
            textRef.current = element
          }}
          aria-labelledby={`${name}-label`}
          value={String(draft ?? '')}
          disabled={disabled}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => onCommit(draft)}
        />
      )}

      {hint && <span className="tc-field-hint">{hint}</span>}
      {multiline && assist.panel}
    </div>
  )
}

/** Teacher-facing names for layout values. "grid_2" is not something a teacher
 *  should ever read. Mirrors OPTION_LAYOUT_LABELS in app/models/enums.py. */
const OPTION_LAYOUT_LABELS: Record<string, string> = {
  list: 'One below the other',
  inline: 'All on one line',
  grid_2: 'Two columns',
  grid_3: 'Three columns',
  grid_4: 'Four columns',
  narrow: 'Narrow',
  standard: 'Standard',
  wide: 'Wide',
  full_page: 'Fill printable width',
}

function isType(property: SchemaProperty, type: string) {
  if (property.type === type) return true
  // Optional fields arrive as anyOf: [{type}, {type: 'null'}].
  return property.anyOf?.some((entry) => entry.type === type) ?? false
}

/** Pydantic emits enums as a `$ref` into `$defs`, sometimes wrapped in allOf or
 *  anyOf when the field is optional. */
function enumChoices(property: SchemaProperty, schema: JsonSchema): string[] | null {
  if (property.enum) return property.enum

  const ref =
    property.$ref ??
    property.allOf?.find((entry) => entry.$ref)?.$ref ??
    property.anyOf?.find((entry) => entry.$ref)?.$ref

  if (!ref) return null
  const definition = schema.$defs?.[ref.replace('#/$defs/', '')]
  return definition?.enum ?? null
}

function humanise(name: string) {
  const words = name.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}
